/**
 * Remote API Helper
 * 
 * Centralise tous les appels API du frontend. 
 * Gère automatiquement le routage vers les PC distants (Workstations) 
 * via le proxy WebSocket du serveur.
 */
import axios from 'axios';

// --- Gestion du Cache des Workstations ---
let _workstations = [];
let _lastWsFetch = 0;

/**
 * Récupère la liste des workstations (avec cache de 30s)
 */
async function getWorkstations() {
  if (Date.now() - _lastWsFetch < 30000 && _workstations.length > 0) return _workstations;
  try {
    const r = await axios.get('/api/workstations');
    if (r.data.success) {
      _workstations = r.data.workstations || [];
      _lastWsFetch = Date.now();
    }
  } catch (e) { /* silent */ }
  return _workstations;
}

export function invalidateWorkstationCache() {
  _lastWsFetch = 0;
}

/**
 * Détermine si un appareil est sur un PC distant
 */
export function getRemoteWsForDevice(device, workstations) {
  if (!device?.workstation_id) return null;
  const currentWsId = workstations.find(w => w.is_current)?.id;
  if (device.workstation_id === currentWsId) return null; // local device

  for (const ws of workstations) {
    if (ws.is_current) continue;
    if (ws.id === device.workstation_id || ws.remote_ws_id === device.workstation_id) {
      return ws;
    }
  }
  return null;
}

/**
 * Fonction maîtresse pour appeler l'API.
 * Si le 'device' est distant, l'appel est routé via le PC principal.
 */
export async function callApiForDevice(device, path, method = 'POST', body = null) {
  if (device) {
    const workstations = await getWorkstations();
    const remoteWs = getRemoteWsForDevice(device, workstations);

    if (remoteWs) {
      console.log(`📡 Routing ${method} ${path} through remote workstation "${remoteWs.name}"`);
      const r = await axios.post(`/api/workstations/${remoteWs.id}/api-proxy`, { method, path, body });
      return r.data?.data || r.data;
    }
  }

  // Appel local direct
  if (method === 'GET') return (await axios.get(path)).data;
  if (method === 'DELETE') return (await axios.delete(path)).data;
  return (await axios({ method, url: path, data: body })).data;
}

/**
 * API unifiée pour les actions courantes.
 * À utiliser en priorité dans les composants React.
 */
export const remoteApi = {
  // --- Appareils & Processus ---
  getDevices: () => callApiForDevice(null, '/api/devices', 'GET'),
  getActiveProcesses: () => callApiForDevice(null, '/api/active-processes', 'GET'),
  stopScript: (deviceId) => callApiForDevice({ workstation_id: null }, `/api/stop-script/${deviceId}`, 'POST'),

  // --- Instagram Actions ---
  startInteraction: (accountId, body = {}) => 
    callApiForDevice(null, `/api/start-interaction/${accountId}`, 'POST', body),
  
  startUnfollow: (accountId, body = {}) => 
    callApiForDevice(null, `/api/start-unfollow/${accountId}`, 'POST', body),

  // --- Workflows ---
  getWorkflows: () => callApiForDevice(null, '/api/v2/workflows', 'GET'),
  createWorkflowSequence: (workflowId, body) => 
    callApiForDevice(null, `/api/workflow-sequences/${workflowId}`, 'POST', body),

  // --- Google / Gmail ---
  startGmail: (body) => callApiForDevice(null, '/api/devices/create-gmail', 'POST', body),
};

export default remoteApi;
